// ============================================================
// DockerCLI.jsx
// ============================================================
// Junior-dev Notes:
// - Design: "Terminal-First" UI.
// - Philosophy: Logic over Graphics. Focus on command syntax.
// - Clean Code: Component-based command items for DRY principle.
// - Branding: prodevconsult.com
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
    IconButton,
    Tooltip
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import TerminalIcon from "@mui/icons-material/Terminal";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TERMINAL_BG = "#011627";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const CodeBlock = styled(Paper)(({ theme }) => ({
    backgroundColor: TERMINAL_BG,
    padding: theme.spacing(2),
    borderRadius: "8px",
    border: `1px solid ${BRAND.border}`,
    fontFamily: "'Fira Code', monospace",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "0.2s",
    "&:hover": { borderColor: DOCKER_BLUE }
}));

/* ── Sub-Component: Command Item ────────────────────────── */
const CommandItem = ({ command, description }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(command);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: BRAND.muted, fontWeight: 500, mb: 1, fontSize: '0.8rem' }}>
                {description}
            </Typography>
            <CodeBlock>
                <Typography variant="body2" sx={{ color: "#ADDB67", fontWeight: 600 }}>
                    <span style={{ color: "rgba(255,255,255,0.3)", marginRight: '8px' }}>$</span>
                    {command}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy Command"}>
                    <IconButton onClick={handleCopy} size="small" sx={{ color: "rgba(255,255,255,0.3)" }}>
                        {copied ? <DoneIcon fontSize="small" sx={{ color: DOCKER_BLUE }} /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </CodeBlock>
        </Box>
    );
};

export default function DockerCLI() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="CLI Mastery · Module 04"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        The <span style={{ color: DOCKER_BLUE }}>Command Line</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        No GUI is as powerful as the CLI. Mastering these commands allows you to
                        automate infrastructure and debug containers like a pro.
                    </Typography>
                </Box>

                {/* 2. THE SYNTAX LOGIC CARD */}
                <Paper sx={{
                    p: 4,
                    mb: 10,
                    bgcolor: "rgba(0,0,0,0.3)",
                    borderLeft: `5px solid ${DOCKER_BLUE}`,
                    borderRadius: "0 16px 16px 0"
                }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        <InfoOutlinedIcon sx={{ color: DOCKER_BLUE, mt: 0.5 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Command Anatomy</Typography>
                            <Typography variant="body1" sx={{ color: BRAND.muted, fontFamily: 'monospace', mb: 2 }}>
                                docker <span style={{ color: DOCKER_BLUE }}>[object]</span> [action] --options
                            </Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                In modern Docker, we use "Management Commands" (e.g., <strong>container</strong>, <strong>image</strong>, <strong>network</strong>)
                                to stay organized.
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* 3. INTERACTIVE COMMAND GRID */}
                <Grid container spacing={6}>
                    {/* CONTAINER OPS */}
                    <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
                            <Box sx={{ p: 1, bgcolor: "rgba(173, 219, 103, 0.1)", borderRadius: '8px' }}>
                                <PlayArrowIcon sx={{ color: "#ADDB67", display: 'block' }} />
                            </Box>
                            <Typography variant="h5" fontWeight={800}>Container Lifecycle</Typography>
                        </Stack>

                        <CommandItem command="docker run -d --name web nginx" description="Run a detached container with a custom name." />
                        <CommandItem command="docker ps" description="List all currently active containers." />
                        <CommandItem command="docker exec -it <id> bash" description="Enter a running container's terminal." />
                        <CommandItem command="docker logs -f <id>" description="Follow live output from a container." />
                    </Grid>

                    {/* IMAGE OPS */}
                    <Grid item xs={12} md={6}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
                            <Box sx={{ p: 1, bgcolor: "rgba(13, 183, 237, 0.1)", borderRadius: '8px' }}>
                                <RestartAltIcon sx={{ color: DOCKER_BLUE, display: 'block' }} />
                            </Box>
                            <Typography variant="h5" fontWeight={800}>Image Management</Typography>
                        </Stack>

                        <CommandItem command="docker images" description="View all images stored on your local disk." />
                        <CommandItem command="docker pull postgres:latest" description="Download a specific image version." />
                        <CommandItem command="docker build -t pro-app:v1 ." description="Build an image using the current directory." />
                        <CommandItem command="docker inspect <name>" description="View low-level metadata of an image." />
                    </Grid>
                </Grid>

                {/* 4. SYSTEM PRUNING */}
                <Box sx={{
                    mt: 10,
                    p: 5,
                    textAlign: 'center',
                    bgcolor: "rgba(255, 82, 82, 0.03)",
                    border: "1px dashed rgba(255, 82, 82, 0.3)",
                    borderRadius: 4
                }}>
                    <DeleteForeverIcon sx={{ color: "#FF5252", fontSize: 40, mb: 2 }} />
                    <Typography variant="h5" fontWeight={800} sx={{ color: "#FF5252", mb: 1 }}>Clean Your Workspace</Typography>
                    <Typography variant="body2" sx={{ color: BRAND.muted, mb: 4, maxWidth: 500, mx: 'auto' }}>
                        Docker can consume significant disk space over time with unused layers and stopped containers.
                    </Typography>
                    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                        <CommandItem command="docker system prune -a" description="Remove all unused data (containers, networks, images)." />
                    </Box>
                </Box>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Ready to analyze Image Layers?</Typography>
                    <Link to="/docker-images" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px", // Boxy terminal look
                                boxShadow: `0 4px 14px rgba(13, 183, 237, 0.3)`,
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Continue to Module 05
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        © 2026 {BRAND.domain} · Practical DevOps Series
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
